import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useFormik } from 'formik';
import { AuthHeader } from 'components';
import ResetPassword from 'components/Auth/ResetPassword';
import validations from 'lib/Validations';
import routeConstants from 'routes/routeConstants';
import { PasswordConfirmIdentity } from 'screens';
import { clearRequest, selectAPIRequestErrors, selectIsLoadingAPIRequest } from 'store/ApiClient';
import { App, forgotPasswordRequest } from 'store/App';
import { IStore } from 'store/types';
import { IApiErrorPayloadWithCode } from 'types';
import { formatDateForApi } from 'utils/date';

const PasswordLookup: React.FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [showConfirmIdentity, setShowConfirmIdentity] = useState(false);

  const apiErrors = useSelector((state: IStore) => selectAPIRequestErrors(state, App.FORGOT_PASSWORD_REQUEST)) as IApiErrorPayloadWithCode;
  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, App.FORGOT_PASSWORD_REQUEST));

  const onNewForm = useCallback(() => {
    dispatch(clearRequest(App.FORGOT_PASSWORD_REQUEST));
  }, [dispatch]);

  const { values, errors, touched, isValid, dirty, handleSubmit, getFieldProps, setFieldValue } = useFormik({
    initialValues: {
      identity: '',
      dateOfBirth: '',
    },
    onSubmit: () => {
      dispatch(forgotPasswordRequest({ ...values, dateOfBirth: formatDateForApi(values.dateOfBirth) }, onSuccess));
    },
    validateOnBlur: true,
    validationSchema: validations.forgotPasswordValidationSchema,
  });

  const onSuccess = () => {
    setShowConfirmIdentity(true);
  };

  const validateField = (field: 'identity' | 'dateOfBirth') => Boolean(touched[field] && errors[field]);

  const onBack = () => {
    setShowConfirmIdentity(false);
  };

  const authHeader = <AuthHeader title="Reset Password" subtitle="Enter the email you use to log in to reset your password." onBack={() => history.push(routeConstants.public.invites.login)} />;

  return showConfirmIdentity ? (
    <PasswordConfirmIdentity onBack={onBack} onSubmitUrl={routeConstants.public.invites.resetPasswordCreateNew} resendPayload={values} />
  ) : (
    <ResetPassword
      formId={App.FORGOT_PASSWORD_REQUEST}
      errors={errors}
      apiErrors={apiErrors}
      authHeader={authHeader}
      onNewForm={onNewForm}
      validateField={validateField}
      setFieldValue={setFieldValue}
      isLoading={isLoading}
      isValid={isValid}
      dirty={dirty}
      handleSubmit={handleSubmit}
      getFieldProps={getFieldProps}
    />
  );
};

export default PasswordLookup;
